import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import Chart from "chart.js/auto";
import "./SchoolDashboard.css";

const buildGroups = (stats) => [
  {
    title: "Academic Overview",
    accent: "#6366f1",
    cards: [
      { label: "Students",  value: stats.total_students,  icon: "🎓", gradient: "linear-gradient(135deg,#667eea,#764ba2)", path: "/school-admin/students" },
      { label: "Teachers",  value: stats.total_teachers,  icon: "👩‍🏫", gradient: "linear-gradient(135deg,#4facfe,#00f2fe)", path: "/school-admin/teachers" },
      { label: "Classes",   value: stats.total_classes,   icon: "🏛️", gradient: "linear-gradient(135deg,#f7971e,#ffd200)", path: "/school-admin/classes" },
      { label: "Sections",  value: stats.total_sections,  icon: "📋", gradient: "linear-gradient(135deg,#f953c6,#b91d73)", path: "/school-admin/sections" },
      { label: "Subjects",  value: stats.total_subjects,  icon: "📖", gradient: "linear-gradient(135deg,#ff5f6d,#ffc371)", path: "/school-admin/subjects" },
      { label: "Exams",     value: stats.total_exams,     icon: "📝", gradient: "linear-gradient(135deg,#11998e,#38ef7d)", path: "/school-admin/exams" },
      { label: "Notices",   value: stats.total_notices,   icon: "📢", gradient: "linear-gradient(135deg,#3a7bd5,#00d2ff)", path: "/school-admin/notices" },
    ],
  },
  {
    title: "Financial Summary",
    accent: "#10b981",
    cards: [
      { label: "Fees Collected", value: `₹${Number(stats.total_paid_fees || 0).toLocaleString("en-IN")}`,   icon: "💰", gradient: "linear-gradient(135deg,#43e97b,#38f9d7)", path: "/school-admin/fees" },
      { label: "Fees Pending",   value: `₹${Number(stats.total_pending_fees || 0).toLocaleString("en-IN")}`, icon: "⏳", gradient: "linear-gradient(135deg,#fa709a,#fee140)", path: "/school-admin/fees" },
    ],
  },
  {
    title: "Salary & Staff",
    accent: "#8b5cf6",
    cards: [
      { label: "Paid Teacher Salary",    value: `₹${Number(stats.paid_teacher_salaries || 0).toLocaleString("en-IN")}`,    icon: "💵", gradient: "linear-gradient(135deg,#43e97b,#38f9d7)", path: "/school-admin/salary/paid" },
      { label: "Pending Teacher Salary", value: `₹${Number(stats.pending_teacher_salaries || 0).toLocaleString("en-IN")}`, icon: "🔔", gradient: "linear-gradient(135deg,#f093fb,#f5576c)", path: "/school-admin/salary/pending" },
      { label: "Total Staff",              value: stats.total_staff,              icon: "👥", gradient: "linear-gradient(135deg,#a18cd1,#fbc2eb)", path: "/school-admin/staff" },
      { label: "Paid Staff Salary",        value: `₹${Number(stats.paid_staff_salary || 0).toLocaleString("en-IN")}`,   icon: "✅", gradient: "linear-gradient(135deg,#4facfe,#00f2fe)", path: "/school-admin/staff" },
      { label: "Unpaid Staff Salary",      value: `₹${Number(stats.unpaid_staff_salary || 0).toLocaleString("en-IN")}`, icon: "❗", gradient: "linear-gradient(135deg,#f7971e,#ffd200)", path: "/school-admin/staff" },
    ],
  },
];

const QUICK_LINKS = [
  { label: "Add Student",      icon: "🎓", path: "/school-admin/students/add" },
  { label: "Add Teacher",      icon: "👩‍🏫", path: "/school-admin/teachers/add" },
  { label: "Fee Dashboard",    icon: "💳", path: "/school-admin/fees" },
  { label: "Generate Salary",  icon: "💰", path: "/school-admin/salary/generate" },
  { label: "Timetable",        icon: "🗓️", path: "/school-admin/timetable" },
  { label: "Library",          icon: "📚", path: "/school-admin/library" },
  { label: "Transport",        icon: "🚌", path: "/school-admin/transport/buses" },
  { label: "Exams",            icon: "📝", path: "/school-admin/exams" },
  { label: "Hostel",           icon: "🏠", path: "/school-admin/hostels" },
  { label: "Inventory",        icon: "📦", path: "/school-admin/inventory" },
  { label: "Events",           icon: "🎉", path: "/school-admin/events" },
  { label: "Attendance",       icon: "✅", path: "/school-admin/teacher-attendance" },
];

export default function SchoolAdminDashboard() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin"))?.school;
  const navigate = useNavigate();

  const [filter, setFilter] = useState("month");
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [storage, setStorage] = useState({ used_mb: 0, free_mb: 0, total_mb: 10240 });
  const [stats, setStats] = useState({
    total_students: 0, total_teachers: 0, total_classes: 0,
    total_sections: 0, total_subjects: 0, total_paid_fees: 0,
    total_pending_fees: 0, total_exams: 0, total_notices: 0,
    paid_teacher_salaries: 0, pending_teacher_salaries: 0,
    total_staff: 0, paid_staff_salary: 0, unpaid_staff_salary: 0,
    max_students_allowed: 500, enrollment_remaining: 500,
    graph_data: { labels: [], received: [], pending: [] },
  });
  const chartRef = useRef(null);

  const loadStats = async () => {
    try {
      const res = await API.get(`/school-admin/stats/?filter=${filter}&year=${selectedYear}`, {
        headers: { "X-SCHOOL-ID": school?.id },
      });
      setStats(res.data);
    } catch (err) {
      console.error("Dashboard stats error:", err);
    }
  };

  const loadStorage = async () => {
    try {
      const res = await API.get("/school-admin/storage/", {
        headers: { "X-SCHOOL-ID": school?.id },
      });
      setStorage(res.data);
    } catch (err) {
      console.error("Storage error:", err);
    }
  };

  useEffect(() => {
    loadStats();
    loadStorage();
  }, [filter, selectedYear]);

  useEffect(() => {
    if (!stats?.graph_data?.labels?.length) return;
    const canvas = document.getElementById("feesGraph");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: stats.graph_data.labels,
        datasets: [
          {
            label: "Fees Received",
            data: stats.graph_data.received,
            backgroundColor: "rgba(67,233,123,0.15)",
            borderColor: "#43e97b",
            fill: true,
            tension: 0.4,
            borderWidth: 3,
            pointBackgroundColor: "#43e97b",
            pointRadius: 5,
            pointHoverRadius: 8,
          },
          {
            label: "Fees Pending",
            data: stats.graph_data.pending,
            backgroundColor: "rgba(250,112,154,0.15)",
            borderColor: "#fa709a",
            fill: true,
            tension: 0.4,
            borderWidth: 3,
            pointBackgroundColor: "#fa709a",
            pointRadius: 5,
            pointHoverRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: { color: "#e2e8f0", font: { size: 13, weight: "bold" }, padding: 20 },
          },
          tooltip: {
            backgroundColor: "rgba(15,23,42,0.9)",
            titleColor: "#f8fafc",
            bodyColor: "#94a3b8",
            borderColor: "rgba(255,255,255,0.1)",
            borderWidth: 1,
            padding: 12,
          },
        },
        scales: {
          x: { ticks: { color: "#64748b" }, grid: { color: "rgba(255,255,255,0.04)" } },
          y: { ticks: { color: "#64748b" }, grid: { color: "rgba(255,255,255,0.04)" } },
        },
      },
    });

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [stats]);

  const storagePercent = Math.min(Math.round((storage.used_mb / storage.total_mb) * 100) || 0, 100);
  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="sd-page">

      {/* ── HERO ── */}
      <div className="sd-hero">
        <div className="sd-hero-left">
          <div className="sd-hero-icon">🏫</div>
          <div>
            <h1 className="sd-hero-name">{school?.name || "School Panel"}</h1>
            <p className="sd-hero-sub">
              <span className="sd-pill">{school?.subdomain}.navith.cloud</span>
              <span className="sd-dot-sep">•</span>
              {today}
            </p>
          </div>
        </div>

        <div className="sd-filter-group">
          {["day", "week", "month", "year"].map((t) => (
            <button
              key={t}
              className={`sd-filter-btn${filter === t ? " active" : ""}`}
              onClick={() => setFilter(t)}
            >
              {t.toUpperCase()}
            </button>
          ))}
          {filter === "year" && (
            <select
              className="sd-year-sel"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {Array.from({ length: 15 }, (_, i) => currentYear - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* ── ENROLLMENT LIMIT ── */}
      {(() => {
        const max = stats.max_students_allowed || 500;
        const enrolled = stats.total_students || 0;
        const remaining = stats.enrollment_remaining ?? Math.max(0, max - enrolled);
        const pct = Math.min(Math.round((enrolled / max) * 100) || 0, 100);
        const isFull = remaining === 0;
        return (
          <section className="sd-section">
            <div className="sd-section-hdr" style={{ "--a": isFull ? "#ef4444" : "#6366f1" }}>
              <span className="sd-section-dot" />
              <h2 className="sd-section-title">Student Enrollment Limit</h2>
            </div>
            <div className="sd-storage-card">
              <div className="sd-storage-row">
                <span className="sd-storage-emoji">{isFull ? "🚫" : "🎓"}</span>
                <div className="sd-storage-text">
                  <p className="sd-storage-heading">
                    {isFull ? "Enrollment Full — No new students can be added" : "Enrollment Capacity"}
                  </p>
                  <p className="sd-storage-sub">
                    <strong style={{ color: "#a78bfa" }}>{enrolled}</strong> Enrolled
                    &nbsp;•&nbsp;
                    <strong style={{ color: isFull ? "#f87171" : "#34d399" }}>{remaining}</strong> Remaining
                    &nbsp;•&nbsp;
                    <strong style={{ color: "#94a3b8" }}>{max}</strong> Max Allowed
                  </p>
                </div>
                <p className="sd-storage-pct" style={{ color: isFull ? "#f87171" : undefined }}>{pct}%</p>
              </div>
              <div className="sd-bar-bg">
                <div
                  className="sd-bar-fill"
                  style={{
                    width: `${pct}%`,
                    background: isFull
                      ? "linear-gradient(90deg,#f97316,#ef4444)"
                      : pct > 80
                      ? "linear-gradient(90deg,#f59e0b,#f97316)"
                      : "linear-gradient(90deg,#6366f1,#a78bfa)",
                  }}
                />
              </div>
              <p className="sd-storage-note">
                {isFull
                  ? "Contact super admin to increase the enrollment limit."
                  : `${remaining} more student${remaining === 1 ? "" : "s"} can be enrolled.`}
              </p>
            </div>
          </section>
        );
      })()}

      {/* ── STAT GROUPS ── */}
      {buildGroups(stats).map((group) => (
        <section key={group.title} className="sd-section">
          <div className="sd-section-hdr" style={{ "--a": group.accent }}>
            <span className="sd-section-dot" />
            <h2 className="sd-section-title">{group.title}</h2>
          </div>
          <div className="sd-grid">
            {group.cards.map((card) => (
              <div
                key={card.label}
                className="sd-card"
                style={{ background: card.gradient }}
                onClick={() => navigate(card.path)}
              >
                <span className="sd-card-bg-icon">{card.icon}</span>
                <p className="sd-card-label">{card.label}</p>
                <p className="sd-card-value">{card.value ?? 0}</p>
                <span className="sd-card-arrow">→</span>
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* ── STORAGE ── */}
      <section className="sd-section">
        <div className="sd-section-hdr" style={{ "--a": "#6366f1" }}>
          <span className="sd-section-dot" />
          <h2 className="sd-section-title">Storage</h2>
        </div>
        <div className="sd-storage-card">
          <div className="sd-storage-row">
            <span className="sd-storage-emoji">💾</span>
            <div className="sd-storage-text">
              <p className="sd-storage-heading">Cloud Storage Usage</p>
              <p className="sd-storage-sub">
                {storage.used_mb} MB used &nbsp;•&nbsp; {storage.free_mb} MB free &nbsp;•&nbsp; {storage.total_mb} MB total
              </p>
            </div>
            <p className="sd-storage-pct">{storagePercent}%</p>
          </div>
          <div className="sd-bar-bg">
            <div
              className="sd-bar-fill"
              style={{
                width: `${storagePercent}%`,
                background: storagePercent > 80
                  ? "linear-gradient(90deg,#f97316,#ef4444)"
                  : "linear-gradient(90deg,#6366f1,#a78bfa)",
              }}
            />
          </div>
          <p className="sd-storage-note">Includes students, teachers, gallery &amp; all uploaded files</p>
        </div>
      </section>

      {/* ── CHART ── */}
      <section className="sd-section">
        <div className="sd-section-hdr" style={{ "--a": "#06b6d4" }}>
          <span className="sd-section-dot" />
          <h2 className="sd-section-title">Fees Collection Analytics</h2>
        </div>
        <div className="sd-chart-card">
          <canvas id="feesGraph" />
        </div>
      </section>

      {/* ── QUICK ACTIONS ── */}
      <section className="sd-section">
        <div className="sd-section-hdr" style={{ "--a": "#f59e0b" }}>
          <span className="sd-section-dot" />
          <h2 className="sd-section-title">Quick Actions</h2>
        </div>
        <div className="sd-quick-grid">
          {QUICK_LINKS.map((l) => (
            <button key={l.path} className="sd-quick-btn" onClick={() => navigate(l.path)}>
              <span className="sd-quick-icon">{l.icon}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      </section>

    </div>
  );
}
